import type { IntegrationProvider } from '@/lib/data/convex/integrations'

/**
 * Pure data for the home landing composition. Product screenshots are
 * carried over from `jason/convert-landing-html-to-next`; the heavy
 * gradient image backdrops were dropped in favor of clean neutral frames
 * for a more minimal B2B SaaS look. Keep this file JSX-free so it can be
 * imported by both server and client section components.
 */

export const LANDING_IMAGE_BASE = '/landing/v2/images'

export const LOOM_EMBED_URL =
  'https://www.loom.com/embed/dc325b42a4b54c50b1e5cdea3964e66a?hideEmbedTopBar=true'

export const HERO_LAUNCH_VIDEO_URL =
  'https://cdn.brew.new/YouTube_Brew-3-0-is-here-Beautiful-On-brand-Emai_Media_5gFEMq1wx60_001_1080p.mp4'

export const HERO_LAUNCH_PREVIEW_WEBM_URL =
  '/landing/v2/videos/brew-launch-teaser.webm'

export const HERO_LAUNCH_PREVIEW_MP4_URL =
  '/landing/v2/videos/brew-launch-teaser.mp4'

export const HERO_LAUNCH_POSTER_URL =
  '/landing/v2/videos/brew-launch-poster.jpg'

export const PRODUCT_HUNT_URL = 'https://www.producthunt.com/products/brew-5'

export type ProductHuntEmbedBadge = {
  href: string
  src: string
  alt: string
  width: number
  height: number
}

/**
 * Official Product Hunt embed badges shown flanking the gold award badge in
 * the testimonials section. The SVGs are served by `api.producthunt.com` and
 * rendered with a native `<img>` (Next/Image cannot optimise these dynamic
 * SVGs).
 */
export const PRODUCT_HUNT_EMBED_BADGES: ReadonlyArray<ProductHuntEmbedBadge> = [
  {
    href: 'https://www.producthunt.com/products/brew-5?embed=true&utm_source=badge-top-post-topic-badge&utm_medium=badge&utm_campaign=badge-brew-4',
    src: 'https://api.producthunt.com/widgets/embed-image/v1/top-post-topic-badge.svg?post_id=1149886&theme=light&period=monthly&topic_id=44&t=1780440931935',
    alt: 'Brew - Like Claude design for email marketing | Product Hunt',
    width: 250,
    height: 54,
  },
  {
    href: 'https://www.producthunt.com/products/brew-5?embed=true&utm_source=badge-top-post-badge&utm_medium=badge&utm_campaign=badge-brew-4',
    src: 'https://api.producthunt.com/widgets/embed-image/v1/top-post-badge.svg?post_id=1149886&theme=light&period=weekly&t=1780440931935',
    alt: 'Brew - Like Claude design for email marketing | Product Hunt',
    width: 250,
    height: 54,
  },
]

export const FOUNDER_CONTACT = {
  phone: '+1-(332)-203-2145',
  phoneHref: 'tel:+13322032145',
  email: 'founders@brew.new',
  emailHref: 'mailto:founders@brew.new',
  callHref: 'https://cal.com/brew',
}

export type AutomationCard = {
  title: string
  description: string
  imageLight: string
  imageDark: string
  alt: string
}

export const AUTOMATION_CARDS: Array<AutomationCard> = [
  {
    title: 'Visual canvas',
    description:
      'Welcome flows, abandoned cart, re-engagement, post-purchase. Build the full revenue lifecycle on one shared canvas.',
    imageLight: '/images/edits-1.png',
    imageDark: '/images/edits-2.png',
    alt: 'Visual automation canvas screen',
  },
  {
    title: 'Event triggers',
    description:
      'Fire from signups, payments, and product events so every revenue moment lands the right email automatically.',
    imageLight: '/images/send-1.png',
    imageDark: '/images/send-2.png',
    alt: 'Event triggers automation screen',
  },
  {
    title: 'Test and analytics',
    description:
      'Preview every step safely, then track opens, clicks, and revenue per send to see which automations pay back.',
    imageLight: '/images/analytics-view-1.png',
    imageDark: '/images/analytics-view-2.png',
    alt: 'Automation test and analytics screen',
  },
]

export type FeaturedIntegration = {
  provider: IntegrationProvider
  name: string
}

/**
 * Featured tiles for the integrations grid. The marks resolve from the
 * real Brew CDN logo map via `IntegrationBrandLogo`, and each tile links
 * to the live `/browse/integrations/<provider>` route. ESPs lead because
 * Brew's positioning is the AI layer that sits on top of the ESP you
 * already run on; the remaining "stack" tools (Stripe, Figma, Clerk,
 * Supabase, Shopify) are still discoverable via the "Explore N+ other
 * tools" link rendered next to the grid.
 */
export const FEATURED_INTEGRATIONS: Array<FeaturedIntegration> = [
  { provider: 'klaviyo', name: 'Klaviyo' },
  { provider: 'hubspot', name: 'HubSpot' },
  { provider: 'mailchimp', name: 'Mailchimp' },
  { provider: 'brevo', name: 'Brevo' },
  { provider: 'braze', name: 'Braze' },
  { provider: 'iterable', name: 'Iterable' },
  { provider: 'mailjet', name: 'Mailjet' },
]

export type TemplateCard = {
  name: string
  tag: string
  variant: 'hero' | 'promo' | 'grid' | 'tallHero' | 'stack'
}

export const TEMPLATE_CARDS: Array<TemplateCard> = [
  { name: 'Welcome series', tag: 'Onboarding', variant: 'hero' },
  { name: 'Black Friday promo', tag: 'Campaign', variant: 'promo' },
  { name: 'Product update', tag: 'Transactional', variant: 'grid' },
  { name: 'Re-engagement', tag: 'Automation', variant: 'tallHero' },
  { name: 'Monthly digest', tag: 'Newsletter', variant: 'stack' },
]

export type Testimonial = {
  quote: string
  name: string
}

export const TESTIMONIAL_COLUMNS: Array<Array<Testimonial>> = [
  [
    {
      quote:
        '@Brew clicked because it is opinionated about the hard last mile: production-ready, on-brand email that renders correctly instead of stopping at a polished mock.',
      name: 'Rohan Chaubey',
    },
    {
      quote:
        '@philip_sorensen love the idea so we can now vibe on email design and automation as well, right?',
      name: 'Mohsin Ali',
    },
    {
      quote:
        'Even Outlook got a laugh because everyone who has shipped email knows that is exactly where pretty previews usually break.',
      name: 'Zolani Matebese',
    },
    {
      quote:
        'Would love my agents to find and buy @Brew for each GTM strategy I run. This goes beyond MCP and moves into MPP territory.',
      name: 'Daniel Baum',
    },
  ],
  [
    {
      quote:
        'Outlook, Gmail clipping, and dark mode are where most AI email tools fall apart. The real question is the rendering discipline underneath, and that is the moat @Brew is taking seriously.',
      name: 'Artem Fedorovich',
    },
    {
      quote:
        'I have been a happy @Brew user for some time now. The team is incredible, the UX is beautiful, and this feels like what modern email marketing should become.',
      name: 'Ohans Emmanuel',
    },
    {
      quote:
        'The walkthrough landed because it used real customer content. Fake demo content rarely proves how a product handles something messy and real.',
      name: 'Margarita Shvetsova',
    },
    {
      quote:
        'Running nurture sequences across different client segments takes real time. Keeping every campaign on-brand at that volume is the pain @Brew speaks to.',
      name: 'Marcelo Macedo',
    },
  ],
  [
    {
      quote:
        'As a solo founder, lifecycle email is exactly the work I keep deferring because the tooling tax is real. The eight-days-to-one-prompt promise lands.',
      name: 'Ferdi Sigona',
    },
    {
      quote:
        '@philip_sorensen can we hook @Brew up to run autonomously, like having an agent analyze churn metrics and generate a custom win-back sequence?',
      name: 'Vikram',
    },
    {
      quote:
        'Brand extraction matters beyond logo and color. The useful version understands fonts, spacing, voice, and enough context to make the email feel like the site.',
      name: 'Yurii Demchenko',
    },
    {
      quote:
        'Curious how @Brew handles sites that block automated readers or render everything client-side. That is where brand extraction usually gets hard.',
      name: 'Francesco Bianchi',
    },
  ],
]
